import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Loader2, FileText, ImageIcon, Ruler,
  StickyNote, Search as SearchIcon, Wrench, Building, HelpCircle, Trash2, FileDigit,
} from 'lucide-react';
import { getProject, listFiles, uploadFiles, deleteFile, updateFileKind, fetchFileAsBlobUrl } from '../api/client';
import { UploadDropzone } from '../components/UploadDropzone';
import { FileTile } from '../components/FileTile';
import { CostMeter } from '../components/CostMeter';
import { OutcomesPanel } from '../components/OutcomesPanel';
import { WorkbenchPanel } from '../components/WorkbenchPanel';

const KIND_META = {
  photo:              { label: 'Photographs',        icon: ImageIcon,   accent: 'text-emerald-400',  border: 'border-emerald-500/30' },
  site_plan:          { label: 'Survey plans',       icon: Ruler,       accent: 'text-cyan-400',     border: 'border-cyan-500/30' },
  site_note:          { label: 'Site notes',         icon: StickyNote,  accent: 'text-amber-400',    border: 'border-amber-500/30' },
  research:           { label: 'Research',           icon: SearchIcon,  accent: 'text-violet-400',   border: 'border-violet-500/30' },
  technical_doc:      { label: 'Technical docs',     icon: Wrench,      accent: 'text-sky-400',      border: 'border-sky-500/30' },
  client_doc:         { label: 'Client docs',        icon: Building,    accent: 'text-rose-400',     border: 'border-rose-500/30' },
  assessment_result:  { label: 'Assessment results', icon: FileDigit,   accent: 'text-slate-300',    border: 'border-slate-500/40' },
  unknown:            { label: 'Unclassified',       icon: HelpCircle,  accent: 'text-slate-400',    border: 'border-slate-500/30' },
};

const KIND_ORDER = [
  'assessment_result', 'photo', 'site_plan', 'site_note',
  'research', 'technical_doc', 'client_doc', 'unknown',
];

export const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const abortRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [p, fs] = await Promise.all([getProject(projectId), listFiles(projectId)]);
      setProject(p);
      setFiles(fs);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error('Project not found');
        navigate('/portal/evidence');
      } else {
        toast.error('Failed to load project');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const all = Array.from(fileList);
    setUploading(true);
    setUploadPct(0);
    abortRef.current = false;

    // Chunk into batches so a single stalled connection doesn't lose 200 files.
    const CHUNK = 20;
    let uploaded = 0;
    try {
      for (let i = 0; i < all.length; i += CHUNK) {
        if (abortRef.current) break;
        const chunk = all.slice(i, i + CHUNK);
        setUploadStatus(`Uploading ${i + 1}–${Math.min(i + CHUNK, all.length)} of ${all.length}…`);
        await uploadFiles(projectId, chunk, (pct) => {
          const overall = Math.round(((uploaded + (pct / 100) * chunk.length) / all.length) * 100);
          setUploadPct(overall);
        });
        uploaded += chunk.length;
        setUploadPct(Math.round((uploaded / all.length) * 100));
      }
      toast.success(`Uploaded ${uploaded} file${uploaded === 1 ? '' : 's'}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadStatus('');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Remove this file from the evidence pack?')) return;
    try {
      await deleteFile(fileId);
      toast.success('File removed');
      refresh();
    } catch {
      toast.error('Failed to remove file');
    }
  };

  const handleReclassify = async (fileId, kind) => {
    try {
      await updateFileKind(fileId, kind);
      toast.success('Reclassified');
      refresh();
    } catch {
      toast.error('Failed to reclassify');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }
  if (!project) return null;

  const grouped = KIND_ORDER
    .map((k) => ({ kind: k, meta: KIND_META[k], items: files.filter((f) => f.kind === k) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6" data-testid="project-detail">
      <div>
        <Link
          to="/portal/evidence"
          data-testid="back-to-dashboard"
          className="text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All projects
        </Link>
        <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight" data-testid="project-name">
              {project.name}
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {project.address ? `${project.address}, ${project.postcode || ''}` : ''}
              {project.job_number && <span className="ml-2 font-mono text-xs">· {project.job_number}</span>}
            </p>
          </div>
          <div className="text-right text-xs text-slate-500 flex flex-col items-end gap-2">
            <CostMeter projectId={projectId} refreshKey={files.length} />
            <div>Files: <span className="text-slate-200 font-medium">{project.total_files}</span></div>
            <div>Status: <span className="uppercase tracking-wider">{project.status.replace('_', ' ')}</span></div>
          </div>
        </div>
      </div>

      <UploadDropzone
        disabled={uploading}
        onFilesSelected={handleFiles}
      />
      {uploading && (
        <div className="border border-slate-800 rounded-lg p-4 bg-slate-900" data-testid="upload-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-300">{uploadStatus}</span>
            <span className="font-mono text-emerald-400">{uploadPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadPct}%` }} />
          </div>
        </div>
      )}

      {files.length === 0 && !uploading ? (
        <Card className="bg-slate-900 border-slate-800 border-dashed">
          <CardContent className="py-14 text-center text-slate-400">
            <p className="text-lg font-medium mb-1">Nothing here yet</p>
            <p className="text-sm">Drop your assessment outputs, photos, plans and research above.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <OutcomesPanel projectId={projectId} filesRefreshKey={files.length} />

          <WorkbenchPanel projectId={projectId} />
          <div className="space-y-8">
          {grouped.map(({ kind, meta, items }) => {
            const Icon = meta.icon;
            return (
              <section key={kind} data-testid={`group-${kind}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-4 h-4 ${meta.accent}`} />
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                    {meta.label}
                  </h2>
                  <span className="text-xs text-slate-500">({items.length})</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {items.map((f) => (
                    <FileTile
                      key={f.id}
                      file={f}
                      onDelete={handleDeleteFile}
                      onReclassify={handleReclassify}
                      fetchThumb={fetchFileAsBlobUrl}
                      accent={meta.accent}
                      border={meta.border}
                    />
                  ))}
                </div>
              </section>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
};
